/**
 * Split-Kennzeichnung in den Listen (Auftrag "Split-Kennzeichnung: Frontend"): ein kleines Icon neben
 * der Bestellnummer. NEUBAU ohne Bestandsvorbild; Styles in `styles/neubau/splitMarke.css`.
 *
 * - Rolle "teil": `split`-Icon + "↳ von 4711". Die Nummer ist ein Knopf und oeffnet die Ursprungs-
 *   bestellung (`onOeffnen`, in der App `lineageOeffnen` - das auch nicht geladene Bestellungen
 *   nachlaedt). Tooltip: "Aus Split von 4711", bei abweichender Wurzel "... · Ursprung 4700".
 * - Rolle "aufgeteilt": `git-fork`-Icon, Tooltip "Aufgeteilt in 4712, 4713".
 * - Rolle "beides" (Mittelglied einer Kette): beide Darstellungen, Tooltip verbunden; jedes Icon
 *   nennt fuer Screenreader nur seinen eigenen Teil.
 *
 * KLICK: `stopPropagation` UND `preventDefault` - die Zeilen tragen eigene Klicks, und manche Listen
 * stecken die Marke in einen Tieflink-Anker (`Verweis`); ohne `preventDefault` folgte der Browser
 * dem `href` des Ankers. TASTATUR: `onKeyDown` stoppt die Blase, weil die Board-Huelle `Bearbeitbar`
 * Enter/Leertaste sonst abfaengt und die Zellenbearbeitung oeffnet.
 *
 * `klickbar={false}` (Nummer nur als Text) setzen Aufrufer, wo der Knopf in einem anderen Knopf bzw.
 * einem Anker stuende (HTML: kein interaktiver Inhalt in `<button>`/`<a>`): die Zeilen der Uebersicht,
 * die selbst `<button>` sind, und die Zeitstrahl-Zeile, deren Anker mit `display: contents` die ganze
 * Zeile umschliesst - ein Geschwister ausserhalb laege ausserhalb des sichtbaren Zeilenkastens.
 * Geoeffnet wird dort ueber den Zeilenklick und den Drawer-Kopf.
 *
 * ANSCHLUSS: `SplitKontext` (von der App aus dem Bestellungsbestand gebaut, einmal, `useMemo`) macht
 * jede Liste zum Einzeiler `<BestellungSplitMarke order={o} />`, ohne den Bestand und
 * `lineageOeffnen` durch jede Seite zu reichen. Ohne Provider (Tests, isolierte Seiten) rendert
 * `BestellungSplitMarke` nichts.
 */
import { createContext, useContext, type MouseEvent, type ReactNode } from "react";
import { Ikone } from "./Ikone.tsx";
import type { Order } from "../lib/types.ts";
import {
  bestellungenNachId,
  splitBezug,
  splitKinderIndex,
  type SplitBezug,
  type SplitKinderIndex,
} from "../lib/splitBezug.ts";

/** Aussage "ist Teil eines Splits"; `null`, wenn die Bestellung nur Quelle ist. */
export function splitTeilText(bezug: SplitBezug): string | null {
  if (bezug.rolle === "aufgeteilt") return null;
  return bezug.wurzel
    ? `Aus Split von ${bezug.ursprung.nummer} · Ursprung ${bezug.wurzel.nummer}`
    : `Aus Split von ${bezug.ursprung.nummer}`;
}

/** Aussage "wurde aufgeteilt"; `null`, wenn die Bestellung nur Teil ist. */
export function splitQuelleText(bezug: SplitBezug): string | null {
  if (bezug.rolle === "teil") return null;
  return `Aufgeteilt in ${bezug.teile.map((t) => t.nummer).join(", ")}`;
}

export function splitTooltip(bezug: SplitBezug): string {
  return [splitTeilText(bezug), splitQuelleText(bezug)].filter(Boolean).join(" · ");
}

export function SplitMarke({
  bezug,
  onOeffnen,
  klickbar = true,
}: {
  bezug: SplitBezug;
  onOeffnen?: (orderId: string) => void;
  klickbar?: boolean;
}): ReactNode {
  const klickbarMitKnopf = bezug.rolle !== "aufgeteilt" && klickbar && !!onOeffnen;
  const teilText = splitTeilText(bezug);
  const quelleText = splitQuelleText(bezug);
  const ursprung = bezug.rolle === "aufgeteilt" ? null : bezug.ursprung;

  function oeffnen(ereignis: MouseEvent): void {
    ereignis.stopPropagation();
    ereignis.preventDefault();
    if (ursprung) onOeffnen?.(ursprung.id);
  }

  const teilIcon = teilText ? (
    // Im Knopf rein dekorativ (der Knopf traegt den Namen). Im Textmodus traegt das Icon die volle Aussage
    // ("Aus Split von ... · Ursprung ..."), dafuer ist der sichtbare Kurztext aria-hidden - nichts doppelt.
    <span
      className="split-marke-icon"
      {...(klickbarMitKnopf ? { "aria-hidden": true } : { role: "img", "aria-label": teilText })}
    >
      <Ikone name="split" />
    </span>
  ) : null;
  const quelleIcon = quelleText ? (
    <span className="split-marke-icon" role="img" aria-label={quelleText}>
      <Ikone name="git-fork" />
    </span>
  ) : null;
  const text = ursprung ? <span className="split-marke-text">{`↳ von ${ursprung.nummer}`}</span> : null;
  const knopfName = ursprung
    ? `Ursprungsbestellung ${ursprung.nummer} öffnen${bezug.rolle !== "aufgeteilt" && bezug.wurzel ? `, Ursprung ${bezug.wurzel.nummer}` : ""}`
    : "";

  // REIHENFOLGE (unabhaengig von `klickbar`): Teil-Teil (Icon + "von ...") VOR dem Quell-Icon. Schrumpft
  // die Marke (Board, schmale Spalte), gibt das hintere Element zuerst nach - das klickbare Icon bleibt.
  return (
    <span className="split-marke" title={splitTooltip(bezug)}>
      {klickbarMitKnopf ? (
        // Das Teil-Icon steht IM Knopf: das Sichtbare bleibt klickbar, zuletzt nur das Icon.
        <button
          type="button"
          className="split-marke-nummer"
          aria-label={knopfName}
          onClick={oeffnen}
          onKeyDown={(ereignis) => ereignis.stopPropagation()}
        >
          {teilIcon}
          {text}
        </button>
      ) : (
        <>
          {teilIcon}
          {ursprung ? <span className="split-marke-nummer" aria-hidden="true">{text}</span> : null}
        </>
      )}
      {quelleIcon}
    </span>
  );
}

// ================= Anschluss an den Bestellungsbestand =================

export interface SplitIndizes {
  index: SplitKinderIndex;
  nachId: ReadonlyMap<string, Order>;
}

export interface SplitKontextWert extends SplitIndizes {
  onOeffnen: (orderId: string) => void;
}

const SplitKontext = createContext<SplitKontextWert | null>(null);

export function baueSplitIndizes(alle: readonly Order[]): SplitIndizes {
  return { index: splitKinderIndex(alle), nachId: bestellungenNachId(alle) };
}

export const SplitKontextProvider = SplitKontext.Provider;

/**
 * Marke fuer eine Bestellung (`order`) bzw. eine Bestell-Id (`orderId`, fuer Listen, die nur die Id
 * fuehren). Nichts, wenn kein Provider da ist, die Bestellung nicht im Bestand steht oder nicht Teil
 * einer Split-Kette ist.
 */
export function BestellungSplitMarke({
  order,
  orderId,
  klickbar = true,
}: {
  order?: Order | null;
  orderId?: string;
  klickbar?: boolean;
}): ReactNode {
  const kontext = useContext(SplitKontext);
  if (!kontext) return null;
  const bestellung = order ?? (orderId ? kontext.nachId.get(orderId) : undefined);
  if (!bestellung) return null;
  const bezug = splitBezug(bestellung, kontext.index, kontext.nachId);
  if (!bezug) return null;
  return <SplitMarke bezug={bezug} onOeffnen={kontext.onOeffnen} klickbar={klickbar} />;
}

/**
 * Ist die Bestellung Teil einer Split-Kette (Rolle teil, aufgeteilt oder beides)? Dieselbe Erkennung
 * wie `BestellungSplitMarke` - genau dann steht neben der Nummer eine Marke. Die Board-Zeile
 * (`Board/Bestellzeile.tsx`) faerbt daran die ganze Zeile orange (`teillieferung-zeile`).
 * Ohne Provider: `false`.
 */
export function useIstTeillieferung(order: Order): boolean {
  const kontext = useContext(SplitKontext);
  return kontext ? splitBezug(order, kontext.index, kontext.nachId) !== null : false;
}
